import { staleRecipeWriteError } from '../recipeWriteConflict';
import { getAccessToken } from '../authentication/supabaseService';
import {
  practiceCreateSubrecipe,
  practiceSubrecipeById,
  practiceUpdateSubrecipe,
  withPracticeSubrecipes,
} from '../../components/onboarding/practice/practiceGuards';
import type { IngredientAlternative } from '../costPrice/costPriceService';
import type { SubrecipeReviewReport } from '../../types/reviewTypes';

export type { IngredientAlternative };

export interface SubrecipeIngredient {
  id: string;
  // Normal-ingredient fields (undefined for nested-subrecipe rows)
  articleId?: string;
  productName?: string;
  quantityNeeded?: number;
  platform?: string;
  customPlatformName?: string;
  quantityInfo?: {
    stdAmount: number;
    stdMultiplier: number;
    stdUnit: string;
  };
  isCustom?: boolean;
  // Per-ingredient waste %. undefined = inherit the restaurant-wide default.
  wastePercentage?: number;
  // Alternative products — allowed ONLY on normal (non-nested-reference) rows.
  // Ignored by cost math; used only by the waste-allocation engine.
  alternatives?: IngredientAlternative[];
  // Nested-subrecipe-reference fields (set when this row references another subrecipe)
  nestedSubrecipeId?: string;
  nestedSubrecipePortionsNeeded?: number;
}

export interface Subrecipe {
  id: string;
  restaurantId: string;
  name: string;
  description?: string;
  portions: number;
  portionsUnit: string;
  ingredients: SubrecipeIngredient[];
  createdAt: Date;
  updatedAt: Date;
  updatedAtToken?: string;
}

export type SubrecipeIngredientInput = Omit<SubrecipeIngredient, 'id'>;

export interface SubrecipeCreateRequest {
  restaurantId: string;
  name: string;
  description?: string;
  portions: number;
  portionsUnit: string;
  ingredients: SubrecipeIngredientInput[];
}

export interface SubrecipeUpdateRequest {
  expectedUpdatedAt?: string;
  id: string;
  name?: string;
  description?: string;
  portions?: number;
  portionsUnit?: string;
  ingredients?: SubrecipeIngredientInput[];
}

export interface SubrecipeUsage {
  usedByCostPrices: { id: string; costPriceName: string }[];
  usedBySubrecipes: { id: string; name: string }[];
}

export interface DeleteSubrecipeCascadeResult {
  affectedCostPrices: number;
  affectedSubrecipes: number;
}

export class SubrecipeCycleApiError extends Error {
  constructor(public parent: string, public child: string) {
    super(`Adding subrecipe ${child} to ${parent} would create a cycle`);
    this.name = 'SubrecipeCycleApiError';
  }
}

export class SubrecipeDepthApiError extends Error {
  constructor(public maxDepth: number) {
    super(`Subrecipe nesting depth would exceed ${maxDepth}`);
    this.name = 'SubrecipeDepthApiError';
  }
}

export class SubrecipeDuplicateNameApiError extends Error {
  constructor() {
    super('A subrecipe with this name already exists');
    this.name = 'SubrecipeDuplicateNameApiError';
  }
}

async function parseWriteError(res: Response): Promise<Error> {
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    /* no JSON body */
  }
  if (res.status === 409 && body?.error === 'stale_parent') return staleRecipeWriteError();
  if (body?.error === 'cycle') {
    return new SubrecipeCycleApiError(body.details?.parent, body.details?.child);
  }
  if (body?.error === 'depth_exceeded') {
    return new SubrecipeDepthApiError(body.maxDepth);
  }
  if (body?.error === 'duplicate_name') {
    return new SubrecipeDuplicateNameApiError();
  }
  // Surface the server's error message (e.g. "Could not create subrecipe") when
  // present so the caller can show something more useful than a bare status.
  const serverMessage = typeof body?.error === 'string' ? body.error : (typeof body?.message === 'string' ? body.message : null);
  return new Error(serverMessage || `${res.status} ${res.statusText}`);
}

export async function fetchSubrecipesByRestaurant(restaurantId: string): Promise<Subrecipe[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/subrecipes/${restaurantId}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to fetch subrecipes: ${res.status} ${res.statusText}`);
  const data = await res.json();
  const subrecipes: Subrecipe[] = data.map((item: any) => ({
    ...item,
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.updatedAt),
  }));
  // Keeps an onboarding practice sauce visible in the picker after it is "saved".
  return withPracticeSubrecipes(restaurantId, subrecipes);
}

export async function fetchSubrecipeById(id: string): Promise<Subrecipe> {
  const practice = practiceSubrecipeById(id);
  if (practice) return practice;
  const token = await getAccessToken();
  const res = await fetch(`/api/subrecipes/subrecipe/${id}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to fetch subrecipe: ${res.status} ${res.statusText}`);
  const data = await res.json();
  return { ...data, createdAt: new Date(data.createdAt), updatedAt: new Date(data.updatedAt) };
}

export async function fetchSubrecipeUsage(id: string): Promise<SubrecipeUsage> {
  const token = await getAccessToken();
  const res = await fetch(`/api/subrecipes/${id}/usage`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to fetch subrecipe usage: ${res.status} ${res.statusText}`);
  return res.json();
}

export async function createSubrecipe(request: SubrecipeCreateRequest, practiceScope = false): Promise<Subrecipe> {
  // Onboarding practice branch: kept in memory, never persisted.
  const practice = practiceCreateSubrecipe(request, practiceScope);
  if (practice) return practice;
  const token = await getAccessToken();
  const res = await fetch(`/api/subrecipes/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(request),
  });
  if (!res.ok) throw await parseWriteError(res);
  const data = await res.json();
  return { ...data, createdAt: new Date(data.createdAt), updatedAt: new Date(data.updatedAt) };
}

export interface SubrecipeMergeResult {
  name: string;
  id: string;
  matchedCount: number;
  unmatched: string[];
  alreadyPresent: { ingredient: string; productName: string }[];
}

export interface SubrecipeTemplateResult {
  created: { name: string; id: string; matchedCount: number; unmatched: string[] }[];
  updated?: SubrecipeMergeResult[];
  unchanged?: SubrecipeMergeResult[];
  failed?: { index: number; name: string; reason: 'stale_preview' | 'duplicate_name' | 'no_matches' | 'import_failed' }[];
  skippedExisting: string[];
  totalCost: number;
  // Per-ingredient match detail driving the review walkthrough. Optional on the
  // type so a response from a server predating the review flow still parses.
  reviewReport?: SubrecipeReviewReport;
}

export interface SubrecipeImportCandidate {
  id: string;
  name: string;
  portions: number;
  portionsUnit: string;
  ingredientCount: number;
  expectedUpdatedAt?: string;
  exact: boolean;
  similarity: number;
}

export interface SubrecipeImportPreviewItem {
  index: number;
  name: string;
  ingredientCount: number;
  portions: number;
  portionsUnit: 'g' | 'ml' | 'p';
  portionsWasGuessed: boolean;
  duplicateOf?: number;
  candidates: SubrecipeImportCandidate[];
}

export interface SubrecipeImportPreview {
  sourceHash: string;
  existingNames: string[];
  items: SubrecipeImportPreviewItem[];
}

export type SubrecipeImportDecision = { index: number; action: 'skip' }
  | { index: number; action: 'create'; name: string; portions: number; portionsUnit: 'g' | 'ml' | 'p' }
  | { index: number; action: 'update'; targetId: string; expectedUpdatedAt: string; portions: number; portionsUnit: 'g' | 'ml' | 'p' };
export interface SubrecipeImportPlan { sourceHash: string; decisions: SubrecipeImportDecision[] }

export class SubrecipeImportApiError extends Error {
  constructor(public code: string) { super(code); }
}

export async function previewSubrecipesFromTemplate(restaurantId: string, file: File): Promise<SubrecipeImportPreview> {
  return uploadSubrecipeTemplate<SubrecipeImportPreview>(restaurantId, file, 'preview');
}

/**
 * Upload a template file (same format as cost-price automation) to bulk-create
 * one subrecipe per item, matching ingredients to order-history products.
 */
export async function createSubrecipesFromTemplate(
  restaurantId: string,
  file: File,
  plan?: SubrecipeImportPlan,
): Promise<SubrecipeTemplateResult> {
  return uploadSubrecipeTemplate<SubrecipeTemplateResult>(restaurantId, file, plan ? 'apply' : undefined, plan);
}

async function uploadSubrecipeTemplate<T>(restaurantId: string, file: File, mode?: 'preview' | 'apply', plan?: SubrecipeImportPlan): Promise<T> {
  const token = await getAccessToken();
  const formData = new FormData();
  formData.append('restaurantId', restaurantId);
  formData.append('file', file);
  if (mode) formData.append('mode', mode);
  if (plan) formData.append('plan', JSON.stringify(plan));

  const res = await fetch(`/api/subrecipes/${mode === 'preview' ? 'previewFromTemplate' : 'createFromTemplate'}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new SubrecipeImportApiError(data.error || 'import_failed');
  }
  return res.json();
}

export async function updateSubrecipe(request: SubrecipeUpdateRequest): Promise<Subrecipe> {
  const practice = practiceUpdateSubrecipe(request);
  if (practice) return practice;
  const token = await getAccessToken();
  const res = await fetch(`/api/subrecipes/update`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(request),
  });
  if (!res.ok) throw await parseWriteError(res);
  const data = await res.json();
  return { ...data, createdAt: new Date(data.createdAt), updatedAt: new Date(data.updatedAt) };
}

/**
 * Delete a subrecipe.
 *   - Without cascade: returns 204 on success. Throws a UsageConflictError carrying the
 *     usage payload if the subrecipe is in use (409 response).
 *   - With cascade: removes references from all parents transactionally.
 */
export class UsageConflictError extends Error {
  constructor(public usage: SubrecipeUsage) {
    super('Subrecipe is referenced by other items');
    this.name = 'UsageConflictError';
  }
}

export async function deleteSubrecipe(
  id: string,
  cascade: boolean = false
): Promise<DeleteSubrecipeCascadeResult | void> {
  const token = await getAccessToken();
  const url = cascade ? `/api/subrecipes/${id}?cascade=true` : `/api/subrecipes/${id}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (res.status === 409) {
    const usage = (await res.json()) as SubrecipeUsage;
    throw new UsageConflictError(usage);
  }
  if (!res.ok) throw new Error(`Failed to delete subrecipe: ${res.status} ${res.statusText}`);
  if (cascade) return res.json();
  return undefined;
}
